import React, { useState } from 'react';

import { v4 as uuid } from 'uuid';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import { Chain } from '~/components/Vendors/Chains/types';

import { isSupportedChannelManager } from '~/hooks/apis/connectivity/useDetectMissingRates';
import { useDetectChainMissingProducts, useGetChainProperties } from '~/hooks/apis/reservation/useChainRateDoctor';
import { useGetChains } from '~/hooks/apis/reservation/useGetChains';

// Several chains share a name (e.g. four "Accor"), so match and display the Salesforce ID too
const filterChains = createFilterOptions<Chain>({ stringify: (option) => `${option.name} ${option.sfid}` });

export default function ChainRateDoctor() {
  const [chain, setChain] = useState<Chain | null>(null);
  const { data: chains = [], isLoading: loadingChains, error: chainsError } = useGetChains();
  const { data: properties = [], isFetching, error: propertiesError } = useGetChainProperties(chain?.id);
  const detection = useDetectChainMissingProducts();
  const supported = properties.filter((property) => isSupportedChannelManager(property.channel_manager ?? ''));
  const showConfirmDialog = useConfirmDialog();

  const submit = async () => {
    const confirmed = await showConfirmDialog({
      title: `Run detection for ${supported.length} properties?`,
      description: `This queues missing product detection for every supported property in ${chain.name} (${chain.sfid}). ${properties.length - supported.length} unsupported properties will be skipped. Jobs run in the background and may create missing products.`,
    });
    if (confirmed) {
      detection.mutate({ chainId: chain.id, runId: uuid() });
    }
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Runs detection for Derbysoft and Synxis properties. Missing products may be created according to existing rate
        rules and eligibility checks.
      </Typography>

      <Stack spacing={2}>
        <Autocomplete
          options={chains}
          value={chain}
          loading={loadingChains}
          disabled={detection.isPending}
          getOptionLabel={(option) => option.name}
          filterOptions={filterChains}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_event, value) => {
            setChain(value);
            detection.reset();
          }}
          renderOption={({ key, ...props }, option) => (
            <Box component="li" key={key} {...props}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Stack spacing={0.25} sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {option.sfid}
                  </Typography>
                </Stack>
                {!option.is_active && <Chip label="Inactive" size="small" />}
              </Stack>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Chain" size="small" helperText={chain?.sfid} />}
        />
        {(chainsError || propertiesError) && <Alert severity="error">Unable to load chain properties.</Alert>}
        {chain && !isFetching && !propertiesError && (
          <Alert severity="info">
            {supported.length} supported properties. {properties.length - supported.length} properties will be skipped
            because their channel manager is unsupported.
          </Alert>
        )}
        <Button
          variant="contained"
          disabled={!chain || isFetching || !!propertiesError || supported.length === 0 || detection.isPending}
          onClick={submit}
        >
          {detection.isPending ? 'Submitting…' : `Run for ${supported.length} properties`}
        </Button>
        {detection.error && <Alert severity="error">Unable to confirm submission: {detection.error.message}</Alert>}
        {detection.data && (
          <Alert severity={detection.data.failedPropertyIds.length > 0 ? 'warning' : 'success'}>
            Submitted: {detection.data.submittedPropertyIds.length}. Failed to confirm:{' '}
            {detection.data.failedPropertyIds.length}. Skipped: {detection.data.skippedPropertyIds.length}. Submitted
            jobs continue in the background; product creation is not yet confirmed.
          </Alert>
        )}
      </Stack>
    </>
  );
}
